import { Line, LineChart, ReferenceLine, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@libs/zapi-ui/shadcn";
import type { TBlockRender, TTableBlock } from "@/zapi-frontend/shared/types/contentBlocks";
import { parseNumericCell } from "@/zapi-frontend/shared/lib/contentBlocks";
import { refForCell } from "@/zapi-frontend/shared/lib/figureRefs";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { CHART_INK, ChartBlockFrame, displayValueFormatter } from "./_ChartBlockFrame";
import { TableBlock } from "./_TableBlock";
import { FigureTrigger } from "./FigureRefs/FigureTrigger";
import { memoType } from "./memoTypography";

const MAX_ROW_MEASURES = 3;
const ROW_CHART_HEIGHT = 60;
const GRID_CHART_HEIGHT = 80;
const GRID_COLUMNS = 3;
const DOT_RADIUS = 2.75;

type TPoint = { period: string; display: string; value: number; figureKey: string | undefined };
type TMeasure = { name: string; points: TPoint[]; hasZeroBaseline: boolean };

// Measures share periods but not units, so each measure gets its own small chart. Up to
// three measures stack as rows; more than that fill a three-column grid.
export function SeriesBlock({ block, render }: { block: TTableBlock; render: TBlockRender }) {
  const periods = block.cols.slice(1);
  const zeroBaseline = new Set(render.zeroBaseline ?? []);
  const measures: TMeasure[] = block.rows
    .map((row, index) => ({
      name: row[0],
      hasZeroBaseline: zeroBaseline.has(index),
      points: periods
        .map((period, i) => ({
          period,
          display: row[i + 1] ?? "",
          value: parseNumericCell(row[i + 1] ?? ""),
          figureKey: refForCell(block.refs, index, i + 1)?.key
        }))
        .filter((point): point is TPoint => point.value !== null)
    }))
    .filter((measure) => measure.points.length >= 2);
  // A measure needs two points to draw a line. The rows that have fewer stay in the table.
  if (measures.length === 0) return <TableBlock block={block} />;

  return (
    <ChartBlockFrame
      label={block.label}
      note={render.note}
      cols={block.cols}
      rows={block.rows}
      refs={block.refs}
      skipped={block.rows.length - measures.length}
    >
      {measures.length <= MAX_ROW_MEASURES ? (
        <SparklineRows measures={measures} />
      ) : (
        <SparklineGrid measures={measures} />
      )}
    </ChartBlockFrame>
  );
}

function LatestValue({ measure }: { measure: TMeasure }) {
  const latest = measure.points[measure.points.length - 1];
  return <FigureTrigger figureKey={latest.figureKey}>{latest.display}</FigureTrigger>;
}

function SparklineRows({ measures }: { measures: TMeasure[] }) {
  return (
    <div data-testid="sparkline-rows">
      {measures.map((measure, i) => (
        <div
          key={i}
          className="grid grid-cols-[170px_minmax(0,1fr)_auto] items-center gap-4 border-b border-innoveraZinc-150 py-4 last:border-b-0"
        >
          <div className={memoType.chartLabel}>{measure.name}</div>
          <Sparkline measure={measure} height={ROW_CHART_HEIGHT} />
          <div className={cn("min-w-[34px] text-right", memoType.chartValue)}>
            <LatestValue measure={measure} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SparklineGrid({ measures }: { measures: TMeasure[] }) {
  const lastRowStart = Math.floor((measures.length - 1) / GRID_COLUMNS) * GRID_COLUMNS;

  return (
    <div className="grid grid-cols-3" data-testid="sparkline-grid">
      {measures.map((measure, i) => {
        const column = i % GRID_COLUMNS;
        return (
          <div
            key={i}
            className={cn(
              "flex min-w-0 flex-col gap-3 py-4",
              column === 0 ? "pr-4" : column === GRID_COLUMNS - 1 ? "pl-4" : "px-4",
              column > 0 && "border-l border-innoveraZinc-150",
              i < lastRowStart && "border-b border-innoveraZinc-150"
            )}
          >
            <Sparkline measure={measure} height={GRID_CHART_HEIGHT} />
            <div className={cn("flex items-center justify-between gap-3", memoType.chartLabel)}>
              <span className="truncate">{measure.name}</span>
              <span className={cn("shrink-0", memoType.chartValue)}>
                <LatestValue measure={measure} />
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Sparkline({ measure, height }: { measure: TMeasure; height: number }) {
  const config: ChartConfig = { value: { label: measure.name, color: CHART_INK } };
  const lastIndex = measure.points.length - 1;

  return (
    <ChartContainer config={config} className="aspect-auto w-full" style={{ height }}>
      <LineChart data={measure.points} margin={{ top: 6, right: 6, bottom: 6, left: 6 }}>
        <XAxis dataKey="period" hide />
        <YAxis
          hide
          domain={
            measure.hasZeroBaseline
              ? [(min: number) => Math.min(min, 0), (max: number) => Math.max(max, 0)]
              : ["dataMin", "dataMax"]
          }
        />
        {measure.hasZeroBaseline && <ReferenceLine y={0} stroke="var(--innovera-zinc300)" strokeDasharray="4 4" />}
        <ChartTooltip
          cursor={{ stroke: "var(--innovera-zinc200)" }}
          content={<ChartTooltipContent formatter={displayValueFormatter} />}
        />
        <Line
          name={measure.name}
          type="linear"
          dataKey="value"
          stroke="var(--color-value)"
          strokeWidth={1.5}
          strokeLinejoin="round"
          dot={<SparklineDot lastIndex={lastIndex} />}
          isAnimationActive={false}
        />
      </LineChart>
    </ChartContainer>
  );
}

type TSparklineDotProps = { cx?: number; cy?: number; index?: number; lastIndex: number };

// Hollow dots along the line, the latest point filled.
function SparklineDot({ cx, cy, index, lastIndex }: TSparklineDotProps) {
  if (cx === undefined || cy === undefined) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={DOT_RADIUS}
      fill={index === lastIndex ? CHART_INK : "#fff"}
      stroke={CHART_INK}
      strokeWidth={1.5}
    />
  );
}
